import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';

interface Question {
  id: string;
  body: string;
  choices: string[];
}

interface Player {
  id: string;
  nickname: string;
  socketId: string;
  connected: boolean;
  isHost: boolean;
}

interface PlayerScore {
  playerId: string;
  nickname: string;
  score: number;
  rank: number;
}

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  currentQuestion: Question | null;
  players: Player[];
  scores: PlayerScore[];
  gameStarted: boolean;
  gameEnded: boolean;
  joinRoom: (roomId: string, nickname: string, playerId: string) => void;
  submitAnswer: (roomId: string, playerId: string, answerIndex: number, timeRemaining: number) => void;
  resetGameState: () => void;
  disconnect: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

interface SocketProviderProps {
  children: ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<PlayerScore[]>([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);

  useEffect(() => {
    // Initialize socket connection
    const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
    console.log('🔌 Connecting to server:', serverUrl);
    
    const socketInstance = io(`${serverUrl}/game`, {
      path: '/ws/',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    setSocket(socketInstance);

    // Connection event handlers
    socketInstance.on('connect', () => {
      console.log('🔌 Connected to server');
      setConnected(true);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('🔌 Disconnected from server:', reason);
      setConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('🔌 Connection error:', error);
      setConnected(false);
    });

    // Game event handlers
    socketInstance.on('players_updated', (data: { players: Player[] }) => {
      console.log('👥 Players updated:', data.players);
      setPlayers(data.players);
    });

    socketInstance.on('joined_room', (data: { 
      roomId: string; 
      playerId: string;
    }) => {
      console.log('✅ Successfully joined room:', data);
    });

    socketInstance.on('session_reset', () => {
      console.log('🔄 Session reset');
      // Reset game state
      setCurrentQuestion(null);
      setGameStarted(false);
      setGameEnded(false);
      setScores([]);
      
      // Notify PlayerContext to reset session scores
      console.log('🔄 Dispatching sessionReset event to PlayerContext');
      const sessionResetEvent = new CustomEvent('sessionReset');
      window.dispatchEvent(sessionResetEvent);
      console.log('🔄 sessionReset event dispatched');
    });

    socketInstance.on('player_left', (data: { playerId: string }) => {
      console.log('🚪 Player left:', data.playerId);
    });

    socketInstance.on('game_started', (data: { question: Question }) => {
      console.log(`🎮 [${new Date().toISOString()}] Game started with question:`, data.question);
      setCurrentQuestion(data.question);
      setGameStarted(true);
      setGameEnded(false);
    });

    socketInstance.on('next_question', (data: { question: Question }) => {
      console.log(`➡️ [${new Date().toISOString()}] Next question received:`, data.question.id);
      
      // Validate that this is actually a new question
      setCurrentQuestion((prevQuestion) => {
        if (prevQuestion && prevQuestion.id === data.question.id) {
          console.warn(`⚠️ [${new Date().toISOString()}] Duplicate question detected! Same question ID: ${data.question.id}`);
          console.warn('⚠️ Previous question:', prevQuestion);
          console.warn('⚠️ New question:', data.question);
          // Still update to ensure state is correct, but log the warning
        } else {
          console.log(`✅ [${new Date().toISOString()}] Question changed from ${prevQuestion?.id || 'none'} to ${data.question.id}`);
        }
        return data.question;
      });
    });

    socketInstance.on('game_ended', (data: { scores: PlayerScore[] }) => {
      console.log('🏁 Game ended with scores:', data.scores);
      setScores(data.scores);
      setGameEnded(true);
      setCurrentQuestion(null);
    });

    socketInstance.on('error', (error: { message: string }) => {
      console.error('❌ Socket error:', error.message);
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 Cleaning up socket connection');
      socketInstance.disconnect();
    };
  }, []);

  const joinRoom = (roomId: string, nickname: string, playerId: string) => {
    if (socket && connected) {
      console.log('🚪 Joining room:', { roomId, nickname, playerId });
      socket.emit('join_room', { roomId, nickname, playerId });
    } else {
      console.warn('⚠️ Cannot join room - socket not connected');
    }
  };

  const submitAnswer = (roomId: string, playerId: string, answerIndex: number, timeRemaining: number) => {
    if (socket && connected) {
      console.log('📝 Submitting answer:', { roomId, playerId, answerIndex, timeRemaining });
      socket.emit('answer_selected', { roomId, playerId, answerIndex, timeRemaining });
    } else {
      console.warn('⚠️ Cannot submit answer - socket not connected');
    }
  };

  const resetGameState = () => {
    setCurrentQuestion(null);
    setGameStarted(false);
    setGameEnded(false);
    setScores([]);
  };

  const disconnect = () => {
    if (socket) {
      console.log('🔌 Manually disconnecting socket');
      socket.disconnect();
    }
  };

  const value: SocketContextType = {
    socket,
    connected,
    currentQuestion,
    players,
    scores,
    gameStarted,
    gameEnded,
    joinRoom,
    submitAnswer,
    resetGameState,
    disconnect,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
} 